import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';

const pages = [
  { number: 20, title: 'Module 4 Title Page', type: 'content' },
  { number: 21, title: 'Core Tool: The Prioritization Rubric', type: 'content' },
  { number: 22, title: 'Activity: Prioritize the "Pro Tier" Questions', type: 'activity', route: '/elevation/workbook/oqf/module-4/worksheet-4-1' },
  { number: 23, title: 'Activity: Your Key Insights', type: 'activity', route: '/elevation/workbook/oqf/module-4/worksheet-4-2' },
  { number: 24, title: 'Module 4 Notes', type: 'content' }
];

export default function Module4Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--mustard-dim)] border-b border-[var(--mustard)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/elevation/workbook/oqf" className="text-[var(--mustard)] hover:text-[var(--mustard)] text-sm mb-4 inline-block">
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 4: Prioritize Open Questions
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will be able to rank a list of open questions using the <strong>OQF Prioritization Rubric</strong> to find the highest-leverage unknown.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The Prioritization Rubric
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Not all questions are equal. This rubric helps you find the questions that, if answered, unlock the most progress for the least effort.
          </p>
          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)] mb-4">
            <p className="text-2xl font-bold text-[var(--mustard)] text-center mb-2">
              Leverage = (Impact × Dependency) ÷ Effort-to-Learn
            </p>
            <p className="text-sm text-[var(--text-sec)] text-center">
              Scoring: Use a simple <strong>1 (Low), 3 (Medium), 5 (High)</strong> scale for each variable.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Variable</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">What It Means</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Guiding Question</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                <tr>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Impact</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">How much would knowing this change what we do?</td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">"If we knew this today, what would change tomorrow?"</td>
                </tr>
                <tr className="bg-[var(--surface)]">
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Dependency</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">What's waiting on this answer?</td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">"If we don't know this, what work can't move forward?"</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Effort-to-Learn</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">How hard will it be to get a reliable answer? <strong>(Note: 1 = Low Effort, 5 = High Effort)</strong></td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">"Could one person get a directional signal in a week?"</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Module Pages</h2>
          <div className="space-y-2">
            {pages.map((page) => (
              <div key={page.number} className="flex items-center justify-between p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--mustard)] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full font-semibold text-sm">
                    {page.number}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-pri)]">{page.title}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {page.type === 'activity' ? (
                    <Link
                      to={page.route!}
                      className="text-[var(--mustard)] hover:text-[var(--mustard)] font-medium flex items-center gap-1"
                    >
                      Open Activity
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <BookOpen className="w-5 h-5 text-[var(--text-ter)]" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Link to="/elevation/workbook/oqf/module-3" className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]">
            ← Previous Module
          </Link>
          <Link to="/elevation/workbook/oqf/module-4/worksheet-4-1" className="px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] flex items-center gap-2">
            Start Activity
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






